import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { Languages, Loader2, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { unitLabel } from "@/lib/production-i18n";

const I18N = {
  ru: {
    title: "Отчёт по производству", back: "Назад",
    duzo: "Много", malo: "Мало", brak: "Нет",
    send: "ОТПРАВИТЬ ОТЧЁТ", saved: "Отчёт отправлен ✓", err: "Ошибка",
    submitted: "Отчёт за сегодня уже отправлен.",
    empty: "Нет продуктов.",
    prepare: "Что приготовить сегодня",
    reminder: "Напоминание",
    doneTitle: "Готово!",
    doneMsg: "Спасибо, отчёт отправлен. Хорошего дня 👋",
    correct: "Исправить",
    goBack: "На главную",
  },
  th: {
    title: "รายงานการผลิต", back: "กลับ",
    duzo: "เยอะ", malo: "น้อย", brak: "หมด",
    send: "ส่งรายงาน", saved: "ส่งรายงานแล้ว ✓", err: "ผิดพลาด",
    submitted: "ส่งรายงานวันนี้แล้ว",
    empty: "ไม่มีสินค้า",
    prepare: "วันนี้ต้องเตรียม",
    reminder: "เตือน",
    doneTitle: "เรียบร้อย!",
    doneMsg: "ขอบคุณ ส่งรายงานแล้ว ขอให้เป็นวันที่ดี 👋",
    correct: "แก้ไข",
    goBack: "กลับหน้าหลัก",
  },
  pl: {
    title: "Raport produkcji", back: "Wstecz",
    duzo: "Dużo", malo: "Mało", brak: "Brak",
    send: "WYŚLIJ RAPORT", saved: "Raport wysłany ✓", err: "Błąd",
    submitted: "Dzisiejszy raport już został wysłany.",
    empty: "Brak produktów.",
    prepare: "Co przygotować dziś",
    reminder: "Przypomnienia",
    doneTitle: "Gotowe!",
    doneMsg: "Raport wysłany. Miłego dnia 👋",
    correct: "Popraw raport",
    goBack: "Do panelu",
  },
};

const LEVELS = [
  { key: "duzo", cls: "bg-emerald-600 text-white", off: "border-emerald-600 text-emerald-700" },
  { key: "malo", cls: "bg-amber-500 text-white", off: "border-amber-500 text-amber-700" },
  { key: "brak", cls: "bg-red-600 text-white", off: "border-red-600 text-red-700" },
];

export default function ProductionReport() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [lang, setLang] = useState(() => localStorage.getItem("tt_prod_lang") || "ru");
  const t = I18N[lang] || I18N.ru;
  const [cats, setCats] = useState([]);
  const [prods, setProds] = useState([]);
  const [states, setStates] = useState({}); // pid -> {level, to_order, order_qty}
  const [report, setReport] = useState(null);
  const [sugg, setSugg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => { localStorage.setItem("tt_prod_lang", lang); }, [lang]);

  async function load() {
    setLoading(true);
    try {
      const [c, p, r, s] = await Promise.all([
        api.get("/production/categories"),
        api.get("/production/products?is_active=true"),
        api.get("/production/report"),
        api.get("/production/suggestions").catch(() => ({ data: null })),
      ]);
      setCats(c.data); setProds(p.data); setReport(r.data.report); setSugg(s.data);
      const st = {};
      Object.entries(r.data.items || {}).forEach(([pid, v]) => {
        st[pid] = { level: v.level };
      });
      setStates(st);
    } catch (e) { toast.error(e.response?.data?.detail || t.err); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  function nameOf(p) {
    if (lang === "th") return p.name_th || p.name_en || p.name_pl;
    if (lang === "ru") return p.name_ru || p.name_en || p.name_pl;
    return p.name_pl || p.name_en || "";
  }
  function catName(c) {
    if (lang === "th") return c.name_th || c.name_pl;
    if (lang === "ru") return c.name_ru || c.name_pl;
    return c.name_pl;
  }
  function setLevel(pid, level) {
    setStates((s) => ({ ...s, [pid]: { ...(s[pid] || {}), level: (s[pid]?.level === level ? null : level) } }));
  }

  const byCat = useMemo(() => {
    const m = {};
    prods.forEach((p) => { (m[p.category_id || "none"] = m[p.category_id || "none"] || []).push(p); });
    return m;
  }, [prods]);

  async function submit() {
    setBusy(true);
    try {
      const items = prods.map((p) => {
        const s = states[p.id] || {};
        return {
          product_id: p.id,
          level: s.level || null,
          to_order: false,
          order_qty: null,
        };
      });
      await api.post("/production/report", { department: "KUCHNIA", submit: true, items });
      toast.success(t.saved);
      // Po wysłaniu wracamy do panelu — użytkownik nie musi decydować co dalej.
      setTimeout(() => navigate("/dashboard"), 900);
    } catch (e) { toast.error(e.response?.data?.detail || t.err); setBusy(false); }
  }

  // Tryb "podgląd" gdy raport już został wysłany dzisiaj — blokujemy edycję i pokazujemy
  // czysty ekran z opcją korekty. To eliminuje pomyłkę "wysłałem, ale coś dalej widzę".
  const isSubmitted = report?.status === "submitted";
  const [correctMode, setCorrectMode] = useState(false);
  const readOnly = isSubmitted && !correctMode;

  const orderedCats = [...cats].filter((c) => (byCat[c.id] || []).length > 0);

  return (
    <div className="min-h-screen bg-[var(--tt-bg)]" data-testid="production-report-page">
      <header className="sticky top-0 z-30 bg-[var(--tt-surface)]/95 backdrop-blur border-b border-[var(--tt-border)] px-3 py-2.5 flex items-center justify-between gap-2">
        <div className="text-base font-semibold tt-display truncate">{t.title}</div>
        <div className="flex items-center gap-1">
          <Languages className="h-4 w-4 text-[var(--tt-muted)]" />
          <Button variant={lang === "ru" ? "default" : "outline"} size="sm" className="h-9 px-3" onClick={() => setLang("ru")} data-testid="prod-lang-ru">RU</Button>
          <Button variant={lang === "th" ? "default" : "outline"} size="sm" className="h-9 px-3" onClick={() => setLang("th")} data-testid="prod-lang-th">ไทย</Button>
          <Button variant={lang === "pl" ? "default" : "outline"} size="sm" className="h-9 px-3" onClick={() => setLang("pl")} data-testid="prod-lang-pl">PL</Button>
          <Button variant="ghost" size="sm" className="h-9 px-2" onClick={() => navigate("/dashboard")}>{t.back}</Button>
        </div>
      </header>

      {loading ? (
        <div className="py-16 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-[var(--tt-muted)]" /></div>
      ) : readOnly ? (
        // Duży komunikat "Gotowe" — bez wątpliwości że raport został wysłany
        <div className="p-6 flex flex-col items-center justify-center min-h-[70vh] max-w-md mx-auto text-center" data-testid="prod-report-done">
          <CheckCircle2 className="h-24 w-24 text-[var(--tt-success-fg)] mb-4" />
          <div className="text-2xl font-semibold tt-display mb-2">{t.doneTitle}</div>
          <div className="text-[var(--tt-muted)] mb-8">{t.doneMsg}</div>
          <div className="flex flex-col gap-2 w-full">
            <Button className="w-full h-12 text-base font-semibold" onClick={() => navigate("/dashboard")} data-testid="prod-report-goback">
              {t.goBack}
            </Button>
            <Button variant="outline" className="w-full h-11" onClick={() => setCorrectMode(true)} data-testid="prod-report-correct">
              {t.correct}
            </Button>
          </div>
        </div>
      ) : (
        <div className="p-3 pb-28 space-y-4 max-w-2xl mx-auto">
          {sugg && ((sugg.to_prepare && sugg.to_prepare.length > 0) || (sugg.reminders && sugg.reminders.length > 0)) && (
            <div className="rounded-xl border-2 border-[var(--tt-primary)]/40 bg-[var(--tt-surface-2)] p-3" data-testid="prod-suggestions">
              <div className="font-semibold tt-display mb-2">📋 {t.prepare}</div>
              <div className="space-y-1.5">
                {(sugg.to_prepare || []).map((x) => (
                  <div key={x.product_id} className="flex items-center justify-between text-sm">
                    <span>{lang === "th" ? (x.name_th || x.name_en || x.name_pl) : lang === "ru" ? (x.name_ru || x.name_en || x.name_pl) : (x.name_pl)}</span>
                    <span className="tt-mono font-semibold">{x.suggested_qty} {unitLabel(x.unit, lang)}</span>
                  </div>
                ))}
              </div>
              {sugg.reminders && sugg.reminders.length > 0 && (
                <div className="mt-2 pt-2 border-t border-[var(--tt-border)]">
                  <div className="text-xs text-[var(--tt-muted)] mb-1">{t.reminder}</div>
                  <div className="flex flex-wrap gap-1.5">
                    {sugg.reminders.map((x) => (
                      <span key={x.product_id} className="tt-badge tt-sev-warning text-[11px]">
                        {lang === "th" ? (x.name_th || x.name_pl) : lang === "ru" ? (x.name_ru || x.name_pl) : x.name_pl}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          {orderedCats.map((c) => (
            <div key={c.id}>
              <div className="text-sm font-semibold tt-display mb-2">{catName(c)}</div>
              <div className="space-y-2">
                {(byCat[c.id] || []).map((p) => {
                  const s = states[p.id] || {};
                  return (
                    <div key={p.id} className="rounded-xl border border-[var(--tt-border)] bg-[var(--tt-surface)] p-3" data-testid={`prod-tile-${p.id}`}>
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <div className="font-semibold leading-tight">{nameOf(p)}</div>
                        <div className="text-xs text-[var(--tt-muted)] shrink-0">{unitLabel(p.unit, lang)}</div>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        {LEVELS.map((lv) => {
                          const on = s.level === lv.key;
                          return (
                            <button key={lv.key} type="button" onClick={() => setLevel(p.id, lv.key)}
                              className={`h-12 rounded-lg border-2 font-semibold text-sm ${on ? lv.cls : `bg-transparent ${lv.off}`}`}
                              data-testid={`lvl-${p.id}-${lv.key}`}>
                              {t[lv.key]}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          {orderedCats.length === 0 && <div className="text-center text-[var(--tt-muted)] py-10">{t.empty}</div>}
        </div>
      )}

      {!readOnly && (
        <div className="fixed bottom-0 inset-x-0 bg-[var(--tt-surface)] border-t border-[var(--tt-border)] p-3">
          <div className="max-w-2xl mx-auto">
            <Button onClick={submit} disabled={busy || loading} className="w-full h-14 text-base font-semibold" data-testid="prod-report-send">
              {busy ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <Send className="h-5 w-5 mr-2" />}
              {t.send}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
